import React, { useEffect, useState } from 'react';
import { Button, Chip, TextField } from '@mui/material';
import { Add } from '@mui/icons-material';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { readableDescription, referenceLabel } from '../../utils/journalEntryText';
import SearchableSelect from '../common/SearchableSelect';
import { useNavigate } from 'react-router-dom';
import { JournalEntry } from '../../interfaces/accounting.interface';
import { JournalEntryService } from '../../services/journal-entry.service';
import ScrollableTable from '../common/ScrollableTable';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const getStatusColor = (status: string): 'warning' | 'success' | 'error' | 'default' => {
    switch (status) {
        case 'DRAFT': return 'warning';
        case 'POSTED': return 'success';
        case 'VOID': return 'error';
        default: return 'default';
    }
};

const JournalEntryList: React.FC = () => {
    const canManage = usePermission(PERMISSIONS.JOURNAL_ENTRY_MANAGE); // create = JOURNAL_ENTRY_MANAGE server-side
    const canPost = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST); // a contra voucher posts immediately
    const navigate = useNavigate();
    const [entries, setEntries] = useState<JournalEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    // Default: hide future-dated entries so one does not top page 1; clearing the field shows everything.
    const [toDate, setToDate] = useState<string>(toLocalIsoDate(new Date()));
    // The date input updates `toDate` on every keystroke; the list only refetches for the settled value.
    const [appliedToDate, setAppliedToDate] = useState<string>(toDate);
    const [firstLoad, setFirstLoad] = useState(true);
    // Reference-type filter: '' = all, 'MANUAL' = hand-made entries, else one source document type.
    const [referenceType, setReferenceType] = useState<string>('');
    const [referenceTypes, setReferenceTypes] = useState<string[]>([]);
    const referenceOptions = React.useMemo(() => [
        { id: 'MANUAL', name: 'Manual entries' },
        ...referenceTypes.map(t => ({ id: t, name: referenceLabel(t) }))
            .sort((a, b) => a.name.localeCompare(b.name)),
    ], [referenceTypes]);

    const fetchEntries = async (pageNum: number) => {
        try {
            setLoading(true);
            const response = await JournalEntryService.getEntriesByInstitution(pageNum, 10, appliedToDate || undefined, referenceType || undefined);
            setEntries(response.content || []);
            setTotalPages(response.totalPages || 0);
            setPage(response.number || 0);
            setError('');
        } catch (err) {
            setError('Failed to load journal entries');
            console.error('Error fetching journal entries:', err);
        } finally {
            setLoading(false);
            setFirstLoad(false);
        }
    };

    useEffect(() => {
        const handle = setTimeout(() => setAppliedToDate(toDate), 400);
        return () => clearTimeout(handle);
    }, [toDate]);

    useEffect(() => {
        JournalEntryService.getReferenceTypes()
            .then(types => setReferenceTypes(Array.isArray(types) ? types : []))
            .catch(() => setReferenceTypes([])); // the filter still offers "Manual entries"
    }, []);

    useEffect(() => {
        fetchEntries(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedToDate, referenceType]);

    if (loading && firstLoad) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.journalList.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2>Journal Entries</h2>
                {canManage && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {canPost && (
                            <Button
                                data-help="accounting.journalList.contra"
                                variant="outlined"
                                onClick={() => navigate('/accounting/journal-entries/contra')}
                            >
                                Contra voucher
                            </Button>
                        )}
                        <Button
                            data-help="accounting.journalList.create"
                            variant="contained"
                            color="primary"
                            startIcon={<Add />}
                            onClick={() => navigate('/accounting/journal-entries/create')}
                        >
                            Create Journal Entry
                        </Button>
                    </div>
                )}
            </div>

            <div data-help-section="accounting.journalList.filters" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                <TextField data-help="accounting.journalList.dateTo"
                    label="Dated up to"
                    type="date"
                    size="small"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText={toDate ? 'Later-dated entries are hidden' : 'Showing all dates'}
                    sx={{ flex: '1 1 180px', maxWidth: 240 }}
                />
                <SearchableSelect data-help="accounting.journalList.refType"
                    label="Reference type" size="small" placeholder="All types"
                    sx={{ flex: '1 1 240px', maxWidth: 340 }}
                    options={referenceOptions} value={referenceType || null}
                    onChange={id => setReferenceType(id)} getOptionLabel={o => o.name} clearable
                    data-testid="je-reference-type-filter"
                />
            </div>

            {error && <div style={{ color: 'red', marginBottom: '16px' }}>{error}</div>}

            <ScrollableTable>
            <table data-help-section="accounting.journalList.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                    <tr>
                        <th data-help="accounting.journalList.col.number" style={tableHeaderStyle}>Entry Number</th>
                        <th data-help="accounting.journalList.col.date" style={tableHeaderStyle}>Date</th>
                        <th data-help="accounting.journalList.col.description" style={tableHeaderStyle}>Description</th>
                        <th data-help="accounting.journalList.col.status" style={tableHeaderStyle}>Status</th>
                        <th data-help="accounting.journalList.col.debit" style={tableHeaderStyle}>Total Debit</th>
                        <th data-help="accounting.journalList.col.credit" style={tableHeaderStyle}>Total Credit</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.length === 0 ? (
                        <tr>
                            <td style={tableCellStyle} colSpan={6}>No journal entries found</td>
                        </tr>
                    ) : (
                        entries.map(entry => (
                            <tr
                                key={entry.id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/accounting/journal-entries/${entry.id}`)}
                            >
                                <td style={tableCellStyle}>{entry.entryNumber || '-'}</td>
                                <td style={tableCellStyle}>{entry.date ? formatDate(entry.date) : '-'}</td>
                                <td style={tableCellStyle}>{readableDescription(entry.description) || '-'}</td>
                                <td style={tableCellStyle}>
                                    <Chip label={humanizeEnum(entry.status)} color={getStatusColor(entry.status)} size="small" />
                                    {entry.referenceType === 'CONTRA' && (
                                        <Chip label="Contra" size="small" variant="outlined" color="info" sx={{ ml: 1 }} />
                                    )}
                                    {entry.referenceType && entry.referenceType !== 'CONTRA' && (
                                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{referenceLabel(entry.referenceType)}</div>
                                    )}
                                </td>
                                <td style={tableCellStyle}>{formatMoney(entry.totalDebit ?? 0)}</td>
                                <td style={tableCellStyle}>{formatMoney(entry.totalCredit ?? 0)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            </ScrollableTable>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '20px' }}>
                <Button data-help="accounting.journalList.prev"
                    variant="outlined"
                    disabled={page === 0}
                    onClick={() => fetchEntries(page - 1)}
                >
                    Previous
                </Button>
                <span>Page {page + 1} of {totalPages || 1}</span>
                <Button data-help="accounting.journalList.next"
                    variant="outlined"
                    disabled={page >= totalPages - 1}
                    onClick={() => fetchEntries(page + 1)}
                >
                    Next
                </Button>
            </div>
        </div>
    );
};

export default JournalEntryList;
